import { useState, useEffect } from 'react';

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCancelledModal, setShowCancelledModal] = useState(false);
  const [registrationDetails, setRegistrationDetails] = useState<{
    name: string;
    email: string;
    occupancy: string;
  } | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const registration = urlParams.get('registration');
    
    if (registration === 'success') {
      const name = urlParams.get('name') || '';
      const email = urlParams.get('email') || '';
      const occupancy = urlParams.get('occupancy') || '';
      
      setRegistrationDetails({ name, email, occupancy });
      setShowSuccessModal(true);
      
      // Clean URL
      window.history.replaceState({}, '', '/');
    } else if (registration === 'cancelled') {
      setShowCancelledModal(true);
      window.history.replaceState({}, '', '/');
    }
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Get form values
    const firstName = formData.get('firstName') as string;
    const lastName = formData.get('lastName') as string;
    const email = formData.get('email') as string;
    const cellPhone = formData.get('cellPhone') as string;
    const addressLine1 = formData.get('addressLine1') as string;
    const addressLine2 = formData.get('addressLine2') as string;
    const city = formData.get('city') as string;
    const state = formData.get('state') as string;
    const zipCode = formData.get('zipCode') as string;
    const country = formData.get('country') as string;
    const handicapIndex = formData.get('handicapIndex') as string;
    const ghinNumber = formData.get('ghinNumber') as string;
    const gender = formData.get('gender') as string;
    const birthMonth = formData.get('birthMonth') as string;
    const birthDay = formData.get('birthDay') as string;
    const birthYear = formData.get('birthYear') as string;
    const homeClub = formData.get('homeClub') as string;
    const howDidYouHear = formData.get('howDidYouHear') as string;
    const shirtSize = formData.get('shirtSize') as string;
    
    // Combine date of birth
    const dateOfBirth = (birthMonth && birthDay && birthYear) 
      ? `${birthMonth}/${birthDay}/${birthYear}` 
      : '';
    
    const registrationData = {
      firstName,
      lastName,
      email,
      cellPhone,
      addressLine1,
      addressLine2,
      city,
      state,
      zipCode,
      country,
      handicapIndex,
      ghinNumber,
      gender,
      dateOfBirth,
      homeClub,
      howDidYouHear,
      shirtSize,
    };
    
    // Default to double occupancy
    const occupancy = 'double';
    
    try {
      // Send welcome email first
      const emailResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-registration-emails`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            email,
            firstName,
            lastName,
            occupancy,
            registrationData,
            emailType: 'welcome'
          }),
        }
      );

      if (!emailResponse.ok) {
        console.error('Failed to send welcome email');
      }

      // Create Stripe checkout session
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-casa-may-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            occupancy,
            registrationData,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error processing your registration. Please try again or contact us at Info@BucketListGolf.org');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Success Modal */}
      {showSuccessModal && registrationDetails && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Registration Confirmed!</h3>
              <p className="text-gray-600 mb-6">
                Thank you for registering, {registrationDetails.name}! We've sent a confirmation email to {registrationDetails.email}.
              </p>
              <button
                onClick={() => setShowSuccessModal(false)}
                className="bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancelled Modal */}
      {showCancelledModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-information-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Registration Cancelled</h3>
              <p className="text-gray-600 mb-6">
                Your registration was cancelled. If you'd like to complete your registration, please fill out the form again.
              </p>
              <button
                onClick={() => {
                  setShowCancelledModal(false);
                  scrollToSection('registration-form');
                }}
                className="bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Return to Registration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="absolute top-0 left-0 right-0 z-50 bg-transparent">
        <div className="max-w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-4">
              <img 
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/10d9fc1b609b54364963a5ea239520dd.png" 
                alt="Bucket List Golf Logo" 
                className="h-20 w-auto"
              />
              <span className="text-base font-medium italic text-white hidden sm:block">
                Where the game takes us
              </span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('overview')} className="font-medium text-white transition-colors hover:text-teal-300 whitespace-nowrap cursor-pointer">
                Overview
              </button>
              <button onClick={() => scrollToSection('courses')} className="font-medium text-white transition-colors hover:text-teal-300 whitespace-nowrap cursor-pointer">
                Courses
              </button>
              <button onClick={() => scrollToSection('package')} className="font-medium text-white transition-colors hover:text-teal-300 whitespace-nowrap cursor-pointer">
                Package
              </button>
              <button onClick={() => scrollToSection('eticket')} className="font-medium text-white transition-colors hover:text-teal-300 whitespace-nowrap cursor-pointer">
                E-Ticket
              </button>
              <button onClick={() => scrollToSection('registration')} className="font-medium text-white transition-colors hover:text-teal-300 whitespace-nowrap cursor-pointer">
                Register
              </button>
              <button onClick={() => scrollToSection('contact')} className="bg-teal-600 text-white px-6 py-2.5 rounded-full font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                Contact Us
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden cursor-pointer">
              <div className="w-6 h-5 flex flex-col justify-between text-white">
                <span className={`w-full h-0.5 bg-current transition-all ${isMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                <span className={`w-full h-0.5 bg-current transition-all ${isMenuOpen ? 'opacity-0' : ''}`}></span>
                <span className={`w-full h-0.5 bg-current transition-all ${isMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('overview')} className="block w-full text-left py-2 text-gray-700 hover:text-teal-600 cursor-pointer">
                Overview
              </button>
              <button onClick={() => scrollToSection('courses')} className="block w-full text-left py-2 text-gray-700 hover:text-teal-600 cursor-pointer">
                Courses
              </button>
              <button onClick={() => scrollToSection('package')} className="block w-full text-left py-2 text-gray-700 hover:text-teal-600 cursor-pointer">
                Package
              </button>
              <button onClick={() => scrollToSection('eticket')} className="block w-full text-left py-2 text-gray-700 hover:text-teal-600 cursor-pointer">
                E-Ticket
              </button>
              <button onClick={() => scrollToSection('registration')} className="block w-full text-left py-2 text-gray-700 hover:text-teal-600 cursor-pointer">
                Register
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 text-gray-700 hover:text-teal-600 cursor-pointer">
                Contact Us
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/7072dc4b0739e0f36063f66feba57f4b.webp" 
            alt="Teeth of the Dog Golf Course at Casa de Campo"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto w-full">
          <h1 className="text-white text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="text-amber-400 block mb-2 text-2xl md:text-3xl">Bucket List Series Presents</span>
            <span className="block">Jack Goettle's</span>
            <span className="block">Bachelor Party in Paradise</span>
          </h1>
          <p className="text-white text-xl md:text-2xl mb-4">Casa de Campo, Dominican Republic</p>
          <p className="text-white text-2xl md:text-3xl font-semibold mb-10">May 28-31st, 2026</p>
          <button onClick={() => scrollToSection('registration')} className="bg-teal-600 text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer">
            Register Now
          </button>
        </div>
      </section>

      {/* Event Overview */}
      <section id="overview" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-6 text-gray-900">
            An Unforgettable Golf Experience
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto leading-relaxed">
            Join us for three days of championship golf on Pete Dye's legendary courses at one of the Caribbean's most exclusive resorts.
          </p>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-8 bg-gradient-to-br from-teal-50 to-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-calendar-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Event Dates</h3>
              <p className="text-gray-700 text-lg font-medium">May 28th-31st, 2026</p>
              <p className="text-gray-600 mt-2">Three nights, three days of golf</p>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-teal-50 to-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-map-pin-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Location</h3>
              <p className="text-gray-700 text-lg font-medium">Casa de Campo Resort</p>
              <p className="text-gray-600 mt-2">La Romana, Dominican Republic</p>
            </div>

            <div className="text-center p-8 bg-gradient-to-br from-teal-50 to-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">
                ⛳️
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-900">Golf Rounds</h3>
              <p className="text-gray-700 text-lg font-medium">3 Championship Rounds</p>
              <p className="text-gray-600 mt-2">Pete Dye masterpieces</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Details */}
      <section id="courses" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-6 text-gray-900">
            Championship Golf on Three Pete Dye Masterpieces
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            Your golf package includes rounds on three world-class courses, each offering a unique and unforgettable experience.
          </p>

          <div className="space-y-12">
            {/* Teeth of the Dog */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="w-full h-80 md:h-auto">
                  <img 
                    src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/0ca241a2434d94ad6a68309aecf1ad1d.jpeg" 
                    alt="Teeth of the Dog Golf Course"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-teal-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap">
                      #1 Caribbean
                    </span>
                    <span className="bg-amber-500 text-white px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap">
                      #20 World
                    </span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Teeth of the Dog</h3>
                  <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                    Pete Dye's legendary oceanside masterpiece. Seven holes play directly along the Caribbean Sea. Ranked #1 in the Caribbean and #20 in the World Top 100 by Golf Digest. Pete Dye famously quirked: "I created 11 holes here, and God made the other 7."
                  </p>
                  <a href="https://casadecampoliving.com/teeth-of-the-dog-a-sneak-peek-at-the-exciting-renovations/" target="_blank" rel="noopener noreferrer" className="text-teal-600 font-semibold hover:text-teal-700 inline-flex items-center gap-2 cursor-pointer">
                    View renovation details
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Dye Fore */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="w-full h-80 md:h-auto md:order-2">
                  <img 
                    src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/b39338b1acb0a811b31a16e230e03629.jpeg" 
                    alt="Dye Fore Golf Course"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="p-8 md:p-10 flex flex-col justify-center md:order-1">
                  <h3 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Dye Fore</h3>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    Dramatic elevation changes and stunning views of the Chavón River and Caribbean Sea. Another Pete Dye design showcasing his genius for strategic, memorable golf.
                  </p>
                </div>
              </div>
            </div>

            {/* The Links */}
            <div className="bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="w-full h-80 md:h-auto">
                  <img 
                    src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/6491859269c767b697fcdec19f961cf7.jpeg" 
                    alt="The Links Golf Course"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <h3 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">The Links</h3>
                  <p className="text-gray-700 text-lg leading-relaxed">
                    Rolling contours, panoramic resort and sea views, and forgiving fairways make The Links a fun Pete Dye design that channels classic links character with approachable, strategic shot values.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Package Details */}
      <section id="package" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-6 text-gray-900">
            Everything You Need for the Perfect Bachelor Party!
          </h2>
          <p className="text-2xl text-center text-teal-600 font-semibold mb-16">All-Inclusive Package</p>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="w-full h-96 rounded-3xl overflow-hidden shadow-lg">
              <img 
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/47e97b9470cc130dce2a216b35152dfb.webp" 
                alt="Premium Dining Experience at Casa de Campo"
                className="w-full h-full object-cover object-center"
              />
            </div>
            <div className="w-full h-96 rounded-3xl overflow-hidden shadow-lg">
              <img 
                src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/6d60e5be1b024ecc3319bc651483c57a.png" 
                alt="Luxury Villas at Casa de Campo"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Accommodations */}
            <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-2xl shadow-sm">
              <div className="w-14 h-14 bg-teal-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-hotel-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Accommodations</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                  <span>3 nights in one of two four-person villas</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                  <span>One transportation cart per room</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                  <span>Airport transfers included</span>
                </li>
              </ul>
            </div>

            {/* Dining */}
            <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-2xl shadow-sm">
              <div className="w-14 h-14 bg-teal-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-restaurant-line text-2xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Dining</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                  <span>Daily breakfast, lunch, and dinner in gourmet restaurants</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                  <span>One special evening at the Beach Club</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                  <span>Unlimited drinks in all hotel bars, restaurants, and beverage carts</span>
                </li>
              </ul>
            </div>

            {/* Golf & Extras */}
            <div className="bg-gradient-to-br from-teal-50 to-white p-8 rounded-2xl shadow-sm">
              <div className="w-14 h-14 bg-teal-600 rounded-full flex items-center justify-center mb-6 text-3xl">
                ⛳️
              </div>
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Golf & Extras</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                  <span>One round on Teeth of the Dog</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                  <span>One round on Dye Fore</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                  <span>One round on the Links</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                  <span>Golf carts & unlimited range balls</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                  <span>Club storage</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-0.5"></i>
                  <span>All taxes and service charges included</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Registration & Pricing */}
      <section id="registration" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-6 text-gray-900">
            Registration Details
          </h2>
          
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 mb-12">
            <div className="text-center mb-10">
              <p className="text-gray-700 text-xl mb-4">Double Occupancy Package</p>
              <p className="text-gray-600 text-lg mb-6">(2 people sharing a room)</p>
              <div className="text-6xl font-bold text-teal-600 mb-2">$2,069</div>
              <p className="text-gray-600 text-xl">per person</p>
            </div>

            <div className="text-center mb-10">
              <button onClick={() => scrollToSection('registration-form')} className="bg-teal-600 text-white px-12 py-4 rounded-full text-xl font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer">
                Register Now
              </button>
            </div>

            <div className="border-t border-gray-200 pt-8">
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Payment Terms</h3>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start gap-3">
                  <i className="ri-information-line text-teal-600 text-xl mt-0.5"></i>
                  <span>Full payment required at registration</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-information-line text-teal-600 text-xl mt-0.5"></i>
                  <span>If you need to cancel, your payment may be used as a credit for a future stay booked within 12 months from cancellation date</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-information-line text-teal-600 text-xl mt-0.5"></i>
                  <span>A credit email will be sent with the amount and expiration date - must be presented at time of rebooking</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-information-line text-teal-600 text-xl mt-0.5"></i>
                  <span><strong>No cash refunds</strong> - all payments are available as credit for future stays only</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Registration Form */}
          <div id="registration-form" className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <h3 className="text-3xl font-bold mb-8 text-center text-gray-900">Complete Your Registration</h3>
            <form onSubmit={handleFormSubmit}>
              
              {/* Contact Information Section */}
              <div className="mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-teal-600">Contact Information</h4>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="firstName" className="block text-gray-700 font-medium mb-2">First Name *</label>
                    <input 
                      type="text" 
                      id="firstName" 
                      name="firstName" 
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-gray-700 font-medium mb-2">Last Name *</label>
                    <input 
                      type="text" 
                      id="lastName" 
                      name="lastName" 
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-2">Email Address *</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="cellPhone" className="block text-gray-700 font-medium mb-2">Cell Phone *</label>
                  <input 
                    type="tel" 
                    id="cellPhone" 
                    name="cellPhone" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                  />
                </div>
              </div>

              {/* Address Section */}
              <div className="mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-teal-600">Address</h4>
                
                <div className="mb-6">
                  <label htmlFor="addressLine1" className="block text-gray-700 font-medium mb-2">Address Line 1 *</label>
                  <input 
                    type="text" 
                    id="addressLine1" 
                    name="addressLine1" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="addressLine2" className="block text-gray-700 font-medium mb-2">Address Line 2 (Optional)</label>
                  <input 
                    type="text" 
                    id="addressLine2" 
                    name="addressLine2" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="city" className="block text-gray-700 font-medium mb-2">City *</label>
                    <input 
                      type="text" 
                      id="city" 
                      name="city" 
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                    />
                  </div>
                  <div>
                    <label htmlFor="state" className="block text-gray-700 font-medium mb-2">State *</label>
                    <input 
                      type="text" 
                      id="state" 
                      name="state" 
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="zipCode" className="block text-gray-700 font-medium mb-2">Zip Code *</label>
                    <input 
                      type="text" 
                      id="zipCode" 
                      name="zipCode" 
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                    />
                  </div>
                  <div>
                    <label htmlFor="country" className="block text-gray-700 font-medium mb-2">Country *</label>
                    <input 
                      type="text" 
                      id="country" 
                      name="country" 
                      required 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                    />
                  </div>
                </div>
              </div>

              {/* Golf Information Section */}
              <div className="mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-teal-600">Golf Information (Optional)</h4>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="handicapIndex" className="block text-gray-700 font-medium mb-2">Handicap Index</label>
                    <input 
                      type="text" 
                      id="handicapIndex" 
                      name="handicapIndex" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                    />
                  </div>
                  <div>
                    <label htmlFor="ghinNumber" className="block text-gray-700 font-medium mb-2">GHIN Number</label>
                    <input 
                      type="text" 
                      id="ghinNumber" 
                      name="ghinNumber" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="gender" className="block text-gray-700 font-medium mb-2">Gender</label>
                    <select 
                      id="gender" 
                      name="gender" 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base cursor-pointer"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Date of Birth</label>
                    <div className="grid grid-cols-3 gap-2">
                      <select 
                        id="birthMonth" 
                        name="birthMonth" 
                        className="w-full px-2 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base cursor-pointer"
                      >
                        <option value="">Month</option>
                        <option value="01">January</option>
                        <option value="02">February</option>
                        <option value="03">March</option>
                        <option value="04">April</option>
                        <option value="05">May</option>
                        <option value="06">June</option>
                        <option value="07">July</option>
                        <option value="08">August</option>
                        <option value="09">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                      </select>
                      <select 
                        id="birthDay" 
                        name="birthDay" 
                        className="w-full px-2 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base cursor-pointer"
                      >
                        <option value="">Day</option>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                          <option key={day} value={day.toString().padStart(2, '0')}>{day}</option>
                        ))}
                      </select>
                      <select 
                        id="birthYear" 
                        name="birthYear" 
                        className="w-full px-2 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base cursor-pointer"
                      >
                        <option value="">Year</option>
                        {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="homeClub" className="block text-gray-700 font-medium mb-2">Home Club</label>
                  <input 
                    type="text" 
                    id="homeClub" 
                    name="homeClub" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base"
                  />
                </div>
              </div>

              {/* Additional Information Section */}
              <div className="mb-8">
                <h4 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-teal-600">Additional Information</h4>
                
                <div className="mb-6">
                  <label htmlFor="howDidYouHear" className="block text-gray-700 font-medium mb-2">How did you hear about us?</label>
                  <select 
                    id="howDidYouHear" 
                    name="howDidYouHear" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base cursor-pointer"
                  >
                    <option value="">Select an option</option>
                    <option value="Thousand Greens">Thousand Greens</option>
                    <option value="Broken Tee Society">Broken Tee Society</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Word of Mouth">Word of Mouth</option>
                    <option value="Email">Email</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="shirtSize" className="block text-gray-700 font-medium mb-2">Shirt Size</label>
                  <select 
                    id="shirtSize" 
                    name="shirtSize" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-base cursor-pointer"
                  >
                    <option value="">Select Size</option>
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="XXL">XXL</option>
                    <option value="XXXL">XXXL</option>
                  </select>
                </div>
              </div>

              <button 
                type="submit" 
                className="w-full bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Proceed to Payment
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* E-Ticket Guide Section */}
      <section id="eticket" className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-passport-line text-4xl text-white"></i>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Dominican Republic E-Ticket
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Before your trip, you'll need to complete an E-Ticket for entry into the Dominican Republic. It's quick and easy!
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <div className="space-y-6 mb-10">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-lg">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Visit the Official Website</h3>
                  <p className="text-gray-700">Go to the Dominican Republic E-Ticket portal to begin your application.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-lg">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Fill Out Your Information</h3>
                  <p className="text-gray-700">Complete the form with your passport details, travel dates, and accommodation information.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-lg">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Submit & Receive Confirmation</h3>
                  <p className="text-gray-700">After submission, you'll receive a QR code via email. Save it on your phone or print it for entry.</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-white p-6 rounded-2xl mb-8">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-teal-600 text-2xl mt-0.5"></i>
                <div>
                  <p className="text-gray-700 font-medium mb-2">Important:</p>
                  <p className="text-gray-600">Complete your E-Ticket at least 72 hours before your departure. It's free and required for all travelers entering the Dominican Republic.</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <a 
                href="https://casaapril2026.bucketlistgolf.org/#eticket-guide" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-teal-600 text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
              >
                View Complete E-Ticket Guide
                <i className="ri-external-link-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Closing Section */}
      <section className="relative py-32 px-4">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://static.readdy.ai/image/57c982531ce74d8992a8785896e0bafd/7072dc4b0739e0f36063f66feba57f4b.webp" 
            alt="Casa de Campo Paradise"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
            A Once-in-a-Lifetime Opportunity
          </h2>
          <p className="text-xl md:text-2xl text-white mb-8 leading-relaxed">
            Casa de Campo is one of the Caribbean's most exclusive resorts, and Teeth of the Dog is a bucket list course that every passionate golfer dreams of playing. With the recent renovation complete, this is the perfect time to experience Pete Dye's masterpiece in its finest form.
          </p>
          <p className="text-2xl md:text-3xl text-white font-semibold">
            This isn't just a bachelor party - it's an escape to paradise that you'll never forget!
          </p>
        </div>
      </section>

      {/* Contact & Footer */}
      <footer id="contact" className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">Questions? Contact us below.</h2>
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-lg">
              <a href="mailto:Info@BucketListGolf.org" className="flex items-center gap-3 hover:text-teal-100 transition-colors cursor-pointer">
                <i className="ri-mail-line text-2xl"></i>
                <span>Info@BucketListGolf.org</span>
              </a>
              <a href="tel:+14132227701" className="flex items-center gap-3 hover:text-teal-100 transition-colors cursor-pointer">
                <i className="ri-phone-line text-2xl"></i>
                <span>(413) 222-7701</span>
              </a>
            </div>
          </div>

          <div className="border-t border-teal-500 pt-8 text-center">
            <p className="text-lg mb-4">© 2026 Bucket List Golf. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}